import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "489",
  "slug": "what-is-the-continental-divide",
  "title": "What Is the Continental Divide? How a Ridge Decides Where Rivers Flow",
  "excerpt": "A single ridgeline can decide whether a raindrop ends up in the Pacific or the Atlantic. Here is what a continental divide is, how it works, and where the great divides of the world run.",
  "date": "2026-06-19",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1671120707020-7227683338d9?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxtb3VudGFpbiUyMHJpZGdlJTIwcml2ZXJ8ZW58MXwwfHx8MTc4MTg1Njc5MXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A continental divide is a line of high ground that separates river systems flowing toward different oceans. Rain falling on one side drains to one ocean; rain on the other side drains to a different one. The best-known is the Continental Divide of the Americas, which runs along the Rockies and the Andes.",
  "content": [
    {
      "type": "paragraph",
      "text": "Stand in the right spot in the Rocky Mountains and you are at a quiet but remarkable boundary. A raindrop landing a few steps to your west will eventually reach the Pacific Ocean; one landing a few steps east will end up in the Atlantic. No fence marks the line, yet it sorts the continent's water into entirely different destinies. This is a continental divide."
    },
    {
      "type": "heading",
      "text": "What Is a Continental Divide?"
    },
    {
      "type": "paragraph",
      "text": "A drainage divide is simply the high ground separating two areas that drain into different bodies of water. A continental divide is the grandest version of this idea: a line that separates the major river basins flowing toward different oceans. Every river, stream and drop of rain on one side of the divide is funnelled, eventually, toward one ocean; everything on the other side heads toward another."
    },
    {
      "type": "paragraph",
      "text": "Importantly, a divide follows high ground, not a single mountain. It traces the crest of ridges and ranges, weaving along whichever line of high points separates one drainage basin from the next. It can run through dramatic peaks or across surprisingly gentle terrain, as long as that ground sheds water to two different sides."
    },
    {
      "type": "heading",
      "text": "How Water Picks a Side"
    },
    {
      "type": "paragraph",
      "text": "The mechanism is just gravity. Water flows downhill along the steepest path available, gathering into rills, then streams, then rivers, each joining larger ones until they reach the sea. The divide is the ridge from which the land slopes down in two directions. Land on the western flank feeds rivers that run west; land on the eastern flank feeds rivers that run east. The crest itself is the dividing line where the two systems meet back to back."
    },
    {
      "type": "heading",
      "text": "The Great Divides of the World"
    },
    {
      "type": "paragraph",
      "text": "The most famous example is the Continental Divide of the Americas, sometimes called the Great Divide. It runs the length of two continents, following the Rocky Mountains down through North America and the Andes down through South America, separating rivers that drain to the Pacific from those that drain to the Atlantic and the Gulf of Mexico. Other continents have their own principal divides shaped by their major mountain ranges and highlands."
    },
    {
      "type": "paragraph",
      "text": "Some special places sit where three drainage basins meet. At a triple divide point, water can set off toward three different seas depending on exactly where it falls. These rare hydrological junctions are prized by geographers as the meeting place of continental-scale watersheds."
    },
    {
      "type": "heading",
      "text": "Why Divides Matter"
    },
    {
      "type": "paragraph",
      "text": "Continental divides are more than trivia. They shape a surprising amount of how a continent works:"
    },
    {
      "type": "list",
      "items": [
        "They define watersheds, the basins that collect water for the world's great rivers.",
        "They influence where water is plentiful or scarce, and which regions share a river system.",
        "They have shaped human history, guiding trade routes, settlement and the placement of borders.",
        "They help geographers and engineers understand where water — and anything carried in it — will ultimately travel."
      ]
    },
    {
      "type": "heading",
      "text": "A New Way to Read the Map"
    },
    {
      "type": "paragraph",
      "text": "Once you know about divides, a mountain range stops being just a wall of rock and becomes a sorting machine for the planet's water. Tracing which way the rivers run on a map tells you which ocean a region belongs to, hydrologically speaking. It is the kind of structural understanding of landscapes that makes geography click into place — and that turns idle map-gazing, or a round of EarthGuessr, into genuine detective work."
    }
  ]
};
